/**
 * PATH: apps/ai/src/pages/DashboardPage.tsx
 * PURPOSE: Premium AI Hub — 3-panel resizable layout orchestrator
 *
 * FLOW:
 *   ┌──────────────┐  ┌──────────────┐  ┌──────────────┐
 *   │ Intelligence  │  │    Chat       │  │   Evidence   │
 *   │  (left panel) │  │  (center)     │  │ (right panel)│
 *   └──────────────┘  └──────────────┘  └──────────────┘
 *
 * DEPENDENCIES:
 * - dashboard/*: All sub-components and chat engine hook
 * - services/conversationService: Backend persistence
 */

import { useState, useEffect } from 'react';
import { Send, Loader2, Sparkles, Plus, History, Database, MessageSquare, Trash2, ChevronRight, ChevronLeft, XCircle, Paperclip, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { getAuthCookie } from '@quotech/auth';
import { conversationService } from '../services/conversationService';
import { useChatEngine } from './dashboard/use-chat-engine';
import { IntelligenceSidebar } from './dashboard/IntelligenceSidebar';
import { EvidencePanel } from './dashboard/EvidencePanel';
import { WelcomeScreen } from './dashboard/WelcomeScreen';
import { MessageBubble } from './dashboard/MessageBubble';
import { FileUploadModal } from '../components/FileUploadModal';
import type { UploadResult, JobOption } from '../services/upload-service';

export default function DashboardPage() {
  const engine = useChatEngine();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [hasNoData, setHasNoData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function checkContextData() {
      try {
        const token = getAuthCookie();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch('/api/context-builder/stats/', { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && (data.with_context ?? 0) === 0) {
          setHasNoData(true);
        }
      } catch {
        // Non-blocking — if stats endpoint unreachable, allow normal operation
      }
    }
    checkContextData();
    return () => { cancelled = true; };
  }, []);

  const handleUploadComplete = (result: UploadResult, job: JobOption) => {
    setIsUploadOpen(false);
    engine.addLocalMessage(
      `File **${result.filename}** has been uploaded successfully to **Job #${job.jobNumber}** (${job.jobName}).\n\nThe file will be available for AI search after context generation runs in Context Builder.`
    );
  };

  return (
    <div className="flex h-full min-h-0 gap-4 relative">
      {/* Collapse toggle for left panel */}
      {engine.leftCollapsed && (
        <button onClick={() => engine.setLeftCollapsed(false)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-card border border-border rounded-r-lg shadow-md hover:bg-accent transition-colors" title="Show Intelligence Center">
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>
      )}

      {/* Left Panel — Intelligence Center */}
      <div className={cn("flex-shrink-0 transition-all duration-300 ease-in-out hidden xl:block", engine.leftCollapsed ? "w-0 opacity-0 overflow-hidden" : "w-80")}>
        {!engine.leftCollapsed && <IntelligenceSidebar onPromptClick={engine.sendMessage} onCollapse={() => engine.setLeftCollapsed(true)} />}
      </div>

      {/* Center Panel — Chat */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20"><Sparkles className="h-6 w-6" /></div>
            <div><h1 className="text-xl font-bold text-foreground">AI Assistant</h1><p className="text-sm text-muted-foreground">Construction Intelligence</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => engine.setShowHistory(!engine.showHistory)} className={cn("p-2 rounded-lg transition-colors", engine.showHistory ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent")}><History className="h-5 w-5" /></button>
            <button onClick={() => engine.setSkipCache(prev => !prev)} className={cn("p-2 rounded-lg transition-colors", engine.skipCache ? "text-amber-500 bg-amber-500/10" : "text-muted-foreground hover:text-foreground hover:bg-accent")} title={engine.skipCache ? 'Cache OFF' : 'Cache ON'}><Database className="h-5 w-5" /></button>
            <button onClick={engine.startNewChat} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors shadow-sm"><Plus className="h-4 w-4" /> New Chat</button>
          </div>
        </div>

        {/* History Sidebar (overlay with backdrop) */}
        {engine.showHistory && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/30 z-30 backdrop-blur-sm"
              onClick={() => engine.setShowHistory(false)}
            />
            {/* Sidebar */}
            <div className="absolute top-0 left-0 bottom-0 w-80 z-40 bg-card border-r border-border shadow-2xl animate-in slide-in-from-left duration-200 rounded-r-xl flex flex-col">
              <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <History className="w-4 h-4 text-primary" /> Chat History
                </h3>
                <button 
                  onClick={() => engine.setShowHistory(false)} 
                  className="p-1.5 hover:bg-accent rounded-lg transition-colors"
                >
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {engine.conversations.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <MessageSquare className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No conversations yet</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {engine.conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => { engine.setActiveConversationId(conv.id); engine.setShowHistory(false); }}
                        className={cn(
                          "group p-3 rounded-lg cursor-pointer transition-colors",
                          engine.activeConversationId === conv.id 
                            ? "bg-primary/10 border border-primary/20" 
                            : "hover:bg-accent border border-transparent"
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{conv.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{conv.messages.length} messages</p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); engine.deleteConversation(conv.id); }}
                            className="p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-card rounded-xl border border-border overflow-hidden min-h-0">
          {engine.messages.length === 0 ? (
            <div className="flex-1 overflow-y-auto"><WelcomeScreen onPromptClick={engine.sendMessage} /></div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {engine.messages.map(message => (
                <MessageBubble key={message.id} message={message} onCopy={engine.handleCopy} copied={engine.copied} onFeedback={engine.handleFeedback} feedbackSubmitting={engine.feedbackSubmitting} />
              ))}
              <div ref={engine.messagesEndRef} />
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-border flex-shrink-0">
            <form onSubmit={engine.handleSubmit} className="flex gap-3">
              <button type="button" onClick={() => setIsUploadOpen(true)} className="flex-shrink-0 p-3 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Upload file to a job">
                <Paperclip className="h-5 w-5" />
              </button>
              <textarea ref={engine.inputRef} value={engine.input} onChange={(e) => engine.setInput(e.target.value)} onKeyDown={hasNoData ? undefined : engine.handleKeyDown}
                disabled={hasNoData}
                placeholder={hasNoData ? "AI Hub is unavailable — no context data" : "Ask me about jobs, quotes, reports, or anything else..."} rows={1}
                className={cn("flex-1 resize-none bg-accent border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 min-h-[48px] max-h-[120px]", hasNoData && "opacity-50 cursor-not-allowed")} />
              <button type="submit" disabled={hasNoData || !engine.input.trim() || engine.isLoading}
                className={cn("px-5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2")}>
                {engine.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </form>
            <p className="text-[10px] text-muted-foreground text-center mt-2">Press Enter to send · Shift+Enter for new line · <Paperclip className="inline w-3 h-3" /> Upload file</p>
          </div>
        </div>
      </div>

      {/* Collapse toggle for right panel */}
      {engine.rightCollapsed && (
        <button onClick={() => engine.setRightCollapsed(false)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-card border border-border rounded-l-lg shadow-md hover:bg-accent transition-colors" title="Show Evidence Panel">
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
      )}

      {/* Right Panel — Evidence & CoT */}
      <div className={cn("flex-shrink-0 transition-all duration-300 ease-in-out hidden lg:block", engine.rightCollapsed ? "w-0 opacity-0 overflow-hidden" : "w-80")}>
        {!engine.rightCollapsed && (
          <EvidencePanel citations={engine.citations} chainOfThought={engine.chainOfThought} model={engine.currentModel} latencyMs={engine.latencyMs} isLoading={engine.isLoading}
            onCollapse={() => engine.setRightCollapsed(true)}
            privacy={engine.activeConversation?.privacy || engine.conversationPrivacy}
            onPrivacyChange={async (newPrivacy) => {
              if (engine.activeConversationId) {
                const backendId = engine.backendConvMap.get(engine.activeConversationId) || engine.activeConversationId;
                try { await conversationService.update(backendId, { privacy: newPrivacy }); } catch { /* non-blocking */ }
              }
              engine.setConversationPrivacy(newPrivacy);
            }}
          />
        )}
      </div>

      {/* File Upload Modal */}
      <FileUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUploadComplete={handleUploadComplete}
      />

      {/* No Data Modal — blocks all interaction when Context Builder has zero context files */}
      {hasNoData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-7 h-7 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-lg font-bold text-foreground mb-3">No Data Available</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              AI Hub does not have any data so that it can't service at all.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
